function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React from 'react';
import { useGlobalContext } from 'seagull';
import { Callout, ConfirmationModal, TextField, Typography } from 'seagull';
import { useStoreActions } from "../../contexts";
import ExternalLinkButton from "../../components/ExternalLinkButton";
import { howToEditAgreementUrl } from "../../constants";
import styles from "./useTerminateAgreement.module.scss";
import { Controller, useForm } from 'react-hook-form';
import { connectControllerToTextField } from "../../utils/react-hook-form.utils"; // eslint-disable-next-line @typescript-eslint/no-explicit-any

function ConfirmationModalWithVerification(props) {
  const {
    modalProps,
    companyName,
    footerContent,
    dismissModal
  } = props;
  const {
    control,
    handleSubmit
  } = useForm({
    mode: 'all',
    defaultValues: {
      companyName: ''
    }
  });
  return /*#__PURE__*/React.createElement(ConfirmationModal, _extends({}, modalProps, {
    content: companyName ? /*#__PURE__*/React.createElement("div", {
      className: styles.modalContentWrapper
    }, /*#__PURE__*/React.createElement(Typography, null, modalProps.content), /*#__PURE__*/React.createElement(Controller, {
      name: "companyName",
      control: control,
      rules: {
        validate(value) {
          const isValid = companyName.trim().toLowerCase() === value.trim().toLowerCase();
          return isValid || "Company name doesn't match";
        }

      },
      render: p => /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(p), {
        label: "Confirm by entering client's company name",
        "data-testid": "company-name-verification"
      }))
    }), footerContent) : modalProps.content,
    skin: "danger",
    onPrimaryButtonClick: handleSubmit(() => dismissModal(true)),
    onSecondaryButtonClick: () => dismissModal(false)
  }));
}

export function useTerminateAgreement() {
  const {
    api,
    logBI
  } = useStoreActions();
  const globalContext = useGlobalContext();

  async function approveTermination(config) {
    const {
      action,
      successMessage,
      errorMessage,
      modalProps,
      companyName,
      footerContent
    } = config;
    const confirmed = await globalContext.showModal({
      closeOnClickOutside: true,
      shouldBlurBackground: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/React.createElement(ConfirmationModalWithVerification, {
          modalProps: modalProps,
          footerContent: footerContent,
          companyName: companyName,
          dismissModal: dismissModal
        });
      }
    });

    if (confirmed) {
      try {
        await action();
        globalContext.showSuccessToast(successMessage, 'termination-success-toast');
      } catch (err) {
        globalContext.showErrorToast(errorMessage, 'termination-failure-toast');
      }
    }
  }

  return {
    terminateAgreementById: async config => {
      const {
        id,
        customerName,
        postAction
      } = config;
      return approveTermination({
        action: async () => {
          logBI(10573, {});
          await api.relationship.terminateAgreement(id);
          await postAction();
        },
        successMessage: 'Agreement was terminated successfully',
        errorMessage: 'Failed to terminate agreement',
        modalProps: {
          title: 'Terminate the agreement?',
          textAlignCenter: false,
          content: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, null, "Terminating the agreement with", ' ', /*#__PURE__*/React.createElement(Typography, {
            className: styles.customerName,
            as: "span",
            weight: "bold"
          }, customerName), ' ', "will immediately stop new invoices issuance and the initiation of payments for pending invoices."), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(Typography, null, "Note that this does not stop payments already in process."), /*#__PURE__*/React.createElement(Typography, null, "Please ensure all final invoices are settled before proceeding, as terminated agreements cannot be reactivated.")),
          primaryButtonLabel: 'Terminate',
          secondaryButtonLabel: 'Cancel'
        },
        footerContent: /*#__PURE__*/React.createElement(Callout, null, "An existing agreement can be edited to change the scope of work or terms.", ' ', /*#__PURE__*/React.createElement(ExternalLinkButton, {
          openInNewTab: true,
          href: howToEditAgreementUrl
        }, "Learn more about editing an agreement")),
        companyName: customerName
      });
    },
    terminateDraftById: async (id, postAction) => {
      return approveTermination({
        action: async () => {
          await api.draft.deleteById(id);
          await postAction();
        },
        successMessage: 'Draft was discarded successfully',
        errorMessage: 'Failed to discard draft',
        modalProps: {
          title: 'Discard this draft?',
          content: "If you discard this draft it will be removed from the system and you won't be able to access it",
          primaryButtonLabel: 'Discard draft',
          secondaryButtonLabel: 'Keep draft'
        }
      });
    },
    cancelProposalById: async (id, postAction) => {
      return approveTermination({
        action: async () => {
          await api.relationship.cancelProposal(id);
          await postAction();
        },
        successMessage: 'Proposal was canceled successfully',
        errorMessage: 'Failed to cancel proposal',
        modalProps: {
          title: 'Cancel this proposal?',
          content: 'If you cancel this proposal it will be removed from the system and neither you or your client will have access to it.',
          //TODO - update copy
          primaryButtonLabel: 'Cancel proposal',
          secondaryButtonLabel: 'Keep proposal'
        }
      });
    }
  };
}